/**
 * #345 -- the renewal payment link's state, as a label and a badge.
 *
 * Beside `paymentStage.ts` (#391), which it does NOT change: that one speaks
 * about a policy's own pre-issuance payment, this one about paying the RENEWAL
 * of an issued policy on the insurer's own payment page. Shared by the broker
 * renewals list / detail, the web policy page and the Telegram mini-app.
 */
import type { RenewalPaymentState } from '@/types/renewalQueue';

export interface RenewalPaymentStateLabel {
  /** Customer-facing words. */
  label: string;
  /** Desk wording, shorter, for the renewals list column. */
  deskLabel: string;
  /** Key understood by `getStatusBadgeVariant`. */
  badge: string;
  /** Mini-app `Badge` tone. */
  tone: 'neutral' | 'info' | 'success' | 'warning' | 'danger';
}

const STATES: Record<RenewalPaymentState, RenewalPaymentStateLabel> = {
  no_link: { label: 'No payment link yet', deskLabel: 'No link', badge: 'draft', tone: 'neutral' },
  link_active: { label: 'Payment link ready', deskLabel: 'Link sent', badge: 'upcoming', tone: 'info' },
  reported_paid: {
    label: 'Payment reported, being confirmed',
    deskLabel: 'Customer says paid',
    badge: 'pending',
    tone: 'warning',
  },
  paid: { label: 'Renewal paid', deskLabel: 'Paid', badge: 'completed', tone: 'success' },
  expired: { label: 'Payment link expired', deskLabel: 'Link expired', badge: 'failed', tone: 'danger' },
};

export const RENEWAL_PAYMENT_FILTER_OPTIONS: { value: RenewalPaymentState | ''; label: string }[] = [
  { value: '', label: 'Any payment link' },
  { value: 'no_link', label: 'No link' },
  { value: 'link_active', label: 'Link sent' },
  { value: 'reported_paid', label: 'Customer says paid' },
  { value: 'paid', label: 'Paid' },
  { value: 'expired', label: 'Link expired' },
];

export function renewalPaymentStateLabel(
  state: RenewalPaymentState | string | null | undefined
): RenewalPaymentStateLabel | null {
  if (!state) return null;
  return STATES[state as RenewalPaymentState] ?? null;
}

/** Card-level states the customer sees a badge for (a policy with no link shows none). */
export function showsCustomerBadge(state: RenewalPaymentState | string | null | undefined): boolean {
  return state === 'link_active' || state === 'reported_paid' || state === 'paid';
}

/** Error code the server answers an expired link's Renew now with. */
export const RENEWAL_LINK_EXPIRED = 'renewal_link_expired';
export const RENEWAL_LINK_NONE = 'renewal_link_none';

/** The `{error: {code}}` envelope's code, or null. */
export function apiErrorCode(error: unknown): string | null {
  const data = (error as { response?: { data?: { error?: { code?: unknown } } } })?.response?.data;
  const code = data?.error?.code;
  return typeof code === 'string' ? code : null;
}

export const EXPIRED_LINK_MESSAGE =
  'This payment link has expired. Your advisor has been asked for a new one.';
export const NO_LINK_MESSAGE = 'Your advisor will send you a payment link.';
export const REPORTED_MESSAGE =
  'We have told your advisor. You will get your receipt as soon as the insurer confirms.';
